import type { DocumentRead } from "@/types";

/**
 * Spreadsheet-format detection for the document-processing UX.
 *
 * The source-mirrored remap grid (#6101) is a spreadsheet metaphor — columns
 * across the top, source rows beneath — so it only applies to tabular source
 * files (xlsx / xls / csv). PDFs extract by page + bounding box, not header +
 * cell, so they keep the classic schema-tabbed mapping dialog.
 */
const SPREADSHEET_EXTENSIONS = /\.(xlsx|xls|csv)$/i;
const SPREADSHEET_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-excel", // .xls
  "text/csv", // .csv
  "application/csv",
]);

export const isSpreadsheetDocument = (doc?: Pick<DocumentRead, "name" | "object_metadata"> | null): boolean => {
  const mime = doc?.object_metadata?.mime_type ?? "";
  const name = doc?.name ?? "";
  return SPREADSHEET_MIME_TYPES.has(mime) || SPREADSHEET_EXTENSIONS.test(name);
};

const PDF_EXTENSION = /\.pdf$/i;
const PDF_MIME_TYPES = new Set(["application/pdf"]);

/** True for PDF source files — routed to the map-on-document remap (#6119). */
export const isPdfDocument = (doc?: Pick<DocumentRead, "name" | "object_metadata"> | null): boolean => {
  const mime = doc?.object_metadata?.mime_type ?? "";
  const name = doc?.name ?? "";
  return PDF_MIME_TYPES.has(mime) || PDF_EXTENSION.test(name);
};
